import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save } from 'lucide-react';
import apiService from '../services/api';
import useToast from '../hooks/useToast';
import './ETPOS/CommonStyles.css';
import './ETPOS/AddSellStyles.css';
import './CardForm.css';

const AddRoleNew = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { toasts, addToast, removeToast, showSuccess, showError, showWarning } = useToast();
    const editRole = location.state?.role;
    const isReadOnly = editRole?.is_default && editRole?.display_name !== 'Cashier' && !editRole?.name?.startsWith('Cashier#');
    const [loading, setLoading] = useState(false);
    const [permissionGroups, setPermissionGroups] = useState({});
    const [businessId, setBusinessId] = useState(null);
    const [formData, setFormData] = useState({
        name: editRole?.name || '',
        permissions: editRole?.permissions?.map(p => p.name) || [],
        is_default: editRole?.is_default || false
    });
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        loadPermissionGroups();
        loadBusinessId();
    }, []);

    const loadBusinessId = async () => {
        try {
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.data?.business_id || userDetails?.business_id;

            // Fallback to default business_id if not found
            if (!bid) {
      /* console.warn('Business ID not found in user details, using default: 40'); */
                bid = 40;
            }

            setBusinessId(bid);
        } catch (error) {
      /* console.error('Error loading business ID:', error); */
            // Set default business_id on error
            setBusinessId(40);
        }
    };

    const loadPermissionGroups = async () => {
        try {
            setLoading(true);
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.data?.business_id || userDetails?.business_id;

            if (!bid) {
                bid = 40; // Default business_id
            }

            const response = await apiService.getPermissionGroups(bid);
            if (response.success && response.data) {
                setPermissionGroups(response.data);

                const initialExpanded = {};
                Object.keys(response.data).forEach((key, index) => {
                    initialExpanded[key] = index === 0;
                });
                setExpandedGroups(initialExpanded);
            }
        } catch (error) {
      /* console.error('Error loading permission groups:', error); */
            alert('Failed to load permissions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const togglePermission = (permissionName) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permissionName)
                ? prev.permissions.filter(p => p !== permissionName)
                : [...prev.permissions, permissionName];
            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const toggleAllGroups = (expand) => {
        const newExpandedState = {};
        Object.keys(permissionGroups).forEach(key => {
            newExpandedState[key] = expand;
        });
        setExpandedGroups(newExpandedState);
    };

    const toggleGroupPermissions = (groupKey) => {
        const group = permissionGroups[groupKey];
        if (!group || !group.permissions) return;

        const groupPermissionNames = group.permissions.map(p => p.name);
        const allSelected = groupPermissionNames.every(name => formData.permissions.includes(name));

        setFormData(prev => {
            let newPermissions = [...prev.permissions];
            if (allSelected) {
                newPermissions = newPermissions.filter(name => !groupPermissionNames.includes(name));
            } else {
                groupPermissionNames.forEach(name => {
                    if (!newPermissions.includes(name)) {
                        newPermissions.push(name);
                    }
                });
            }
            return { ...prev, permissions: newPermissions };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!businessId) {
            showError('Business ID not found. Please try again.');
            return;
        }

        if (formData.permissions.length === 0) {
            showWarning('Please select at least one permission.');
            return;
        }

        try {
            setLoading(true);
            const roleData = {
                business_id: businessId,
                name: formData.name,
                permissions: formData.permissions,
                is_default: formData.is_default
            };

            let response;
            if (editRole) {
                response = await apiService.updateRole(editRole.id, roleData);
            } else {
                response = await apiService.createRole(roleData);
            }

            if (response.success) {
                showSuccess(editRole ? 'Role updated successfully!' : 'Role created successfully!');
                navigate('/users', { state: { activeTab: 'roles' } });
            } else {
                showError(response.message || 'Failed to save role');
            }
        } catch (error) {
      /* console.error('Error saving role:', error); */
            showError(error.message || 'Failed to save role. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && Object.keys(permissionGroups).length === 0) {
        return (
            <div className="card-form-container">
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Loading permissions...</p>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="card-form-container">
            <div className="page-header" style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button
                    onClick={() => navigate('/users', { state: { activeTab: 'roles' } })}
                    style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        height: '40px'
                    }}
                    title="Back"
                >
                    ←
                </button>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>
                    {editRole ? 'Edit Role' : 'Add Role'}
                </h3>
            </div>

            <form id="roleForm" onSubmit={handleSubmit} className="sell-form" style={{ gap: '0.5rem' }}>
                <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
                    <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
                        <div className="form-group">
                            <label>Role Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={isReadOnly}
                            />
                            {isReadOnly && (
                                <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                    Default roles cannot be renamed or modified
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-section" style={{ padding: '1.5rem', marginBottom: '0', background: 'transparent', border: 'none', boxShadow: 'none' }}>
                    <div className="permissions-top-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--role-title, #1e293b)', margin: 0 }}>Permissions</h3>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: '1.25rem',
                        marginTop: '1.0rem',
                        width: '100%'
                    }}>
                        {Object.entries(permissionGroups).map(([groupKey, group]) => {
                            const groupPermissionNames = group.permissions?.map(p => p.name) || [];
                            const isGroupAllSelected = groupPermissionNames.length > 0 &&
                                groupPermissionNames.every(name => formData.permissions.includes(name));

                            return (
                                <div key={groupKey} style={{
                                    background: 'var(--role-card-bg, #f2f0ff)',
                                    border: '1px solid var(--role-card-border, #dbdeff)',
                                    borderRadius: '8px',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.75rem'
                                }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.6rem',
                                        fontSize: '0.95rem',
                                        fontWeight: '700',
                                        color: 'var(--role-card-text, #1e293b)',
                                        cursor: 'pointer',
                                        margin: 0,
                                        userSelect: 'none'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isGroupAllSelected}
                                            onChange={() => toggleGroupPermissions(groupKey)}
                                            disabled={isReadOnly}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                                accentColor: 'var(--etpos-primary, #4f46e5)',
                                                margin: 0
                                            }}
                                        />
                                        <span>{group.name}</span>
                                    </label>

                                    <div style={{
                                        height: '1px',
                                        background: 'var(--role-divider, #dbdeff)',
                                        margin: '0.25rem 0'
                                    }} />

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.65rem'
                                    }}>
                                        {group.permissions?.map((permission) => {
                                            const isChecked = formData.permissions.includes(permission.name);
                                            return (
                                                <label
                                                    key={permission.name}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.6rem',
                                                        fontSize: '0.85rem',
                                                        fontWeight: isChecked ? '600' : '500',
                                                        color: isChecked ? 'var(--etpos-primary, #4f46e5)' : 'var(--role-item-text, #475569)',
                                                        cursor: 'pointer',
                                                        margin: 0,
                                                        userSelect: 'none',
                                                        transition: 'color 0.2s'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => togglePermission(permission.name)}
                                                        disabled={isReadOnly}
                                                        style={{
                                                            width: '15px',
                                                            height: '15px',
                                                            cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                                            accentColor: '#4f46e5',
                                                            margin: 0
                                                        }}
                                                    />
                                                    <span>{permission.display_name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="action-buttons-section" style={{ margin: '1rem', padding: '0 0.5rem' }}>
                    <div className="action-buttons" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                        {!isReadOnly && (
                            <button
                                type="submit"
                                className="save-btn"
                                disabled={loading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1.25rem',
                                    backgroundColor: loading ? '#94a3b8' : '#3b82f6',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <Save size={16} /> {loading ? 'Saving...' : (editRole ? 'Update Role' : 'Save Role')}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
        </>
    );
};

export default AddRoleNew;
